import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip, faBox, faPlus, faCalculator, faCalendarAlt, faFileAlt, faAnchor, faHandshake, faIdCard, faBuilding, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import systemDataService from '../../services/systemDataService';
import stockService from '../../services/stockService';
import { applyPickedResultToLine, isServiceResult, pickedResultLabel } from '../../components/Invoicing/utils/pickedSearchResult';
import { fillPendingVatFigures, lineVatFigures, resolveVatPercentage, PENDING_FIGURE } from '../../components/Invoicing/utils/vatPercentage';
import InvoiceValidationModal, { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import SearchableSelect from '../../components/Invoicing/SearchableSelect';
import VatRateSelect from '../../components/Invoicing/VatRateSelect';
import InlineProductCreator from '../../components/Stock/InlineProductCreator';
import EditableUnitPriceCell from '../../components/Invoicing/EditableUnitPriceCell';
import BdnQuantityNote from '../../components/Invoicing/BdnQuantityNote';
import { exceedsDecimals } from '../../utils/amountFormat';
import { PRICE_DECIMALS, QUANTITY_DECIMALS, AMOUNT_DECIMALS, stepFor, formatFigure, lineNet } from '../../utils/precision';
import './OutgoingInvoice.css';

/**
 * Screen 2: Products & Line Items for INCOMING INVOICES
 * 
 * Sections:
 * - Line Items Table (dynamic add/remove) - PRODUCTS ONLY
 * - Totals Summary
 * - Gross Amount in Words
 * 
 * Note: No Vessel & Shipment Information for incoming invoices
 */

const IncomingInvoiceScreen2 = ({ 
  invoiceData, 
  setInvoiceData,
  lineItems,
  setLineItems,
  vesselInfo,  // Not used for incoming invoices but kept for compatibility
  setVesselInfo,  // Not used for incoming invoices but kept for compatibility
  onPrevious,
  onNext,
  isReadOnly = false
}) => {
  const [products, setProducts] = useState([]);
  const vatPercentageCache = useRef({});
  const [units, setUnits] = useState([]);
  
  // Currency options
  const currencies = ['USD', 'EUR', 'GBP', 'ZAR', 'JPY', 'CNY', 'AUD', 'CAD', 'CHF', 'INR', 'CZK', 'SGD', 'AED', 'VND'];
  
  // Format number based on selected locale
  const formatNumberByLocale = (value, decimals = 2) => {
    if (!value || isNaN(value)) return decimals === 2 ? '0.00' : '0.0000';
    const num = parseFloat(value);
    const format = invoiceData.number_format || 'US';
    
    if (format === 'EU') {
      // European: 10.000,00
      const formatted = num.toFixed(decimals);
      const parts = formatted.split('.');
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${integerPart},${parts[1]}`;
    } else {
      // US: 10,000.00
      return num.toLocaleString('en-US', { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
      });
    }
  };
  
  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationFieldIds, setValidationFieldIds] = useState([]);
  
  // Line item validation state
  const [lineItemErrors, setLineItemErrors] = useState({});
  
  // Product variant search state (per line item)
  const [variantSearchTerms, setVariantSearchTerms] = useState({});
  const [debouncedVariantSearches, setDebouncedVariantSearches] = useState({});
  const [filteredVariants, setFilteredVariants] = useState({});
  const [showVariantDropdown, setShowVariantDropdown] = useState({});
  const [variantSearchLoading, setVariantSearchLoading] = useState({});
  // Why the last search failed, per line. A failed search must never
  // masquerade as an empty catalogue (see stockService.productSearchErrorMessage).
  const [variantSearchError, setVariantSearchError] = useState({});
  const variantSearchRefs = useRef({});

  // Sprint D — Inline product creator state. When the user clicks "+ Create
  // product" in an empty search dropdown, we open InlineProductCreator with
  // the line index and search term so we know where to attach the new variant.
  const [productCreatorState, setProductCreatorState] = useState({
    isOpen: false,
    lineIndex: null,
    searchTerm: '',
  });
  
  useEffect(() => {
    loadReferenceData();
    
    // Initialize with one line item if empty
    if (lineItems.length === 0) {
      addLineItem();
    }
    
    // Prevent scroll from changing number inputs
    const preventScroll = (e) => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    document.addEventListener('wheel', preventScroll, { passive: true });
    return () => document.removeEventListener('wheel', preventScroll);
  }, []);

  const entityId = invoiceData?.hms_entity;
  const glAccountParams = entityId ? { entity: entityId } : {};
  const vatRateParams = entityId ? { entity_id: entityId } : {};
  
  useEffect(() => {
    // Recalculate totals when line items change
    calculateTotals();
  }, [lineItems]);
  
  // Debounce variant search for each line item
  useEffect(() => {
    const timers = {};
    
    Object.keys(variantSearchTerms).forEach(lineIndex => {
      timers[lineIndex] = setTimeout(() => {
        setDebouncedVariantSearches(prev => ({
          ...prev,
          [lineIndex]: variantSearchTerms[lineIndex]
        }));
      }, 300);
    });
    
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [variantSearchTerms]);
  
  // Fetch variants when debounced search changes
  useEffect(() => {
    Object.keys(debouncedVariantSearches).forEach(lineIndex => {
      const searchTerm = debouncedVariantSearches[lineIndex];
      if (searchTerm && searchTerm.trim().length >= 2) {
        fetchVariantsForLineItem(lineIndex, searchTerm);
      } else {
        setFilteredVariants(prev => ({ ...prev, [lineIndex]: [] }));
      }
    });
  }, [debouncedVariantSearches]);
  
  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(variantSearchRefs.current).forEach(lineIndex => {
        const ref = variantSearchRefs.current[lineIndex];
        if (ref && !ref.contains(event.target)) {
          setShowVariantDropdown(prev => ({ ...prev, [lineIndex]: false }));
        }
      });
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadReferenceData = async () => {
    try {
      const [unitsData] = await Promise.all([
        systemDataService.getUnits()
      ]);

      // Map units to dropdown format, filter only active units
      const formattedUnits = unitsData
        .filter(unit => unit.is_active)
        .map(unit => ({
          value: unit.id,
          label: unit.name
        }));
      setUnits(formattedUnits);
      
      // Resolve capture hint fields (_capture_unit_code, _capture_vat_rate_percentage,
      // _capture_product_id) to actual FK IDs in pre-filled line items, then
      // recalculate VAT amounts.
      const needsResolve = lineItems.some(item =>
        item._capture_unit_code ||
        item._capture_vat_rate_percentage ||
        item._capture_product_id
      );
      if (needsResolve) {
        let allVatRates = [];
        const needsVatResolve = lineItems.some(item => item._capture_vat_rate_percentage);
        if (needsVatResolve) {
          const eid = invoiceData?.hms_entity;
          const ratesData = eid
            ? await invoicingService.fetchVATRatesByEntity(eid)
            : await invoicingService.fetchVATRates();
          allVatRates = Array.isArray(ratesData) ? ratesData : (ratesData.results || []);
        }

        // Sprint C: seed the variant search term so the description cell
        // visibly reflects the auto-linked product. Captured via state-update
        // batch alongside the line item resolve.
        const searchTermSeed = {};

        const resolved = lineItems.map((item, idx) => {
          const updates = { ...item };
          
          if (item._capture_unit_code && !item.unit) {
            const matchedUnit = unitsData.find(
              u => u.is_active && u.code && u.code.toUpperCase() === item._capture_unit_code.toUpperCase()
            );
            if (matchedUnit) updates.unit = matchedUnit.id;
            delete updates._capture_unit_code;
          }
          
          if (item._capture_vat_rate_percentage && !item.vat_rate) {
            const pct = parseFloat(item._capture_vat_rate_percentage);
            if (!isNaN(pct)) {
              let matchedVat = allVatRates.find(v => parseFloat(v.rate_percentage) === pct);
              if (!matchedVat) {
                matchedVat = allVatRates.find(v => Math.abs(parseFloat(v.rate_percentage) - pct) < 0.5);
              }
              if (matchedVat) {
                updates.vat_rate = matchedVat.id;
                vatPercentageCache.current[matchedVat.id] = parseFloat(matchedVat.rate_percentage);
              } else {
                updates.vat_rate = 'CUSTOM';
                updates.custom_vat_percentage = pct;
              }
            }
            delete updates._capture_vat_rate_percentage;
          }

          // Sprint C: hydrate `product` FK from the auto-match annotation and
          // seed the search box display. _capture_product_display_name is
          // already pricing-stripped by the InvoiceCaptureModal helper.
          if (item._capture_product_id && !item.product) {
            updates.product = item._capture_product_id;
            if (item._capture_product_display_name) {
              searchTermSeed[idx] = item._capture_product_display_name;
              updates.item_description = item._capture_product_display_name;
            }
            delete updates._capture_product_id;
            delete updates._capture_product_display_name;
          }

          // Recalculate VAT and gross amounts after resolving the rate, through
          // the one shared rule (vatPercentage.js). A line with no rate keeps
          // the VAT that was captured for it.
          if (updates.vat_rate) {
            Object.assign(updates, lineVatFigures(
              updates.net_amount,
              resolveVatPercentage(updates, vatPercentageCache.current),
            ));
          } else {
            const netAmt = parseFloat(updates.net_amount) || 0;
            updates.gross_amount = (netAmt + parseFloat(updates.vat_amount || 0)).toFixed(2);
          }
          
          return updates;
        });
        setLineItems(resolved);
        if (Object.keys(searchTermSeed).length > 0) {
          setVariantSearchTerms(prev => ({ ...prev, ...searchTermSeed }));
        }
      }
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };
  
  const fetchVariantsForLineItem = async (lineIndex, searchTerm) => {
    try {
      setVariantSearchLoading(prev => ({ ...prev, [lineIndex]: true }));
      
      // Incoming Invoices use purchase_price
      const variants = await stockService.searchVariantsForInvoice(searchTerm, 'INCOMING', { includeServices: true });
      
      setFilteredVariants(prev => ({ ...prev, [lineIndex]: variants }));
      setVariantSearchError(prev => ({ ...prev, [lineIndex]: null }));
      setVariantSearchLoading(prev => ({ ...prev, [lineIndex]: false }));
    } catch (error) {
      console.error('Error fetching variants for line item:', error);
      setFilteredVariants(prev => ({ ...prev, [lineIndex]: [] }));
      setVariantSearchError(prev => ({
        ...prev,
        [lineIndex]: stockService.productSearchErrorMessage(error),
      }));
      setVariantSearchLoading(prev => ({ ...prev, [lineIndex]: false }));
    }
  };
  
  const autoResizeTextarea = (index) => {
    setTimeout(() => {
      const textarea = document.getElementById(`line_item_description_${index}`);
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    }, 0);
  };

  const handleSelectVariant = (lineIndex, variant) => {
    console.log('Selected variant for line', lineIndex, ':', variant);
    
    // Update the line item with variant data
    const updated = [...lineItems];
    const cleanDescription = pickedResultLabel(variant);
    // A picked service fills description + GL account and never the product slot.
    updated[lineIndex] = applyPickedResultToLine(updated[lineIndex], variant, { priceField: 'purchase_price', units });
    
    // Recalculate amounts for this line
    calculateLineItemAmounts(updated, lineIndex, 'unit_price');
    
    setLineItems(updated);
    
    // Update search term to show selected item (without pricing)
    setVariantSearchTerms(prev => ({ 
      ...prev, 
      [lineIndex]: cleanDescription 
    }));
    
    // Close dropdown
    setShowVariantDropdown(prev => ({ ...prev, [lineIndex]: false }));
    
    // Auto-resize textarea to fit content
    autoResizeTextarea(lineIndex);
  };

  // Sprint C — accept a captured "Suggested:" product chip. Behaves as if
  // the user had clicked the variant in the search dropdown: hydrate the
  // line with variant data, seed the search box, and clear the suggestion
  // flag so the chip disappears. We hit /api/stock/stock-variants/<id>/ to
  // fetch full variant details (the suggestion only carries id + display).
  const acceptCaptureSuggestion = async (lineIndex) => {
    const item = lineItems[lineIndex];
    const sugg = item?._capture_product_suggestion;
    if (!sugg?.id) return;

    try {
      const variant = await stockService.getVariantById(sugg.id);
      handleSelectVariant(lineIndex, variant);
    } catch (err) {
      console.error('Failed to load suggested variant', err);
      // Fallback: at least set product id and clear the suggestion so the
      // user isn't stuck. Display name is the pricing-stripped suggestion text.
      const updated = [...lineItems];
      updated[lineIndex] = {
        ...updated[lineIndex],
        // The capture suggestion is products-only; the guard keeps it so.
        product: isServiceResult(sugg) ? null : sugg.id,
        item_description: sugg.display_name || updated[lineIndex].item_description,
        _capture_product_suggestion: null,
      };
      setLineItems(updated);
      setVariantSearchTerms(prev => ({ ...prev, [lineIndex]: sugg.display_name || '' }));
    } finally {
      // handleSelectVariant doesn't know about the chip, so always clear
      // the suggestion flag here regardless of which path ran.
      setLineItems(prev => prev.map((it, idx) =>
        idx === lineIndex ? { ...it, _capture_product_suggestion: null } : it
      ));
    }
  };

  const dismissCaptureSuggestion = (lineIndex) => {
    setLineItems(prev => prev.map((it, idx) =>
      idx === lineIndex ? { ...it, _capture_product_suggestion: null } : it
    ));
  };

  // Sprint D — open the inline product creator for a specific line item.
  // searchTerm is the text the user typed that returned no results, so we
  // can pre-fill it as both subtype_label (Step 1) and shortname (Step 2).
  const openProductCreator = (lineIndex, searchTerm) => {
    setProductCreatorState({
      isOpen: true,
      lineIndex,
      searchTerm: searchTerm || '',
    });
    // Hide the dropdown so the modal doesn't render on top of an open empty state.
    setShowVariantDropdown(prev => ({ ...prev, [lineIndex]: false }));
  };

  const closeProductCreator = () => {
    setProductCreatorState({ isOpen: false, lineIndex: null, searchTerm: '' });
  };

  // Fired when InlineProductCreator's StockItemVariantForm POST succeeds.
  // Mirrors handleSelectVariant (which the user invokes by clicking a search
  // result), so the new variant is attached to the line item exactly the same
  // way an existing variant would be.
  const handleInlineProductCreated = (variant) => {
    const lineIndex = productCreatorState.lineIndex;
    if (lineIndex === null || !variant) {
      closeProductCreator();
      return;
    }
    handleSelectVariant(lineIndex, variant);
    closeProductCreator();
  };
  // HMS request — fired when InlineProductCreator's ServiceQuickForm succeeds.
  // Services have no pricing, so we fill the line's description + default GL
  // account; the operator enters qty / unit price / VAT on the line itself.
  const handleInlineServiceCreated = (service) => {
    const lineIndex = productCreatorState.lineIndex;
    if (lineIndex === null || !service) {
      closeProductCreator();
      return;
    }
    const description = service.description || service.service_type_display || '';
    const updated = [...lineItems];
    updated[lineIndex] = {
      ...updated[lineIndex],
      product: null,
      item_description: description,
      gl_account: service.gl_account || updated[lineIndex].gl_account || null,
    };
    setLineItems(updated);
    setVariantSearchTerms(prev => ({ ...prev, [lineIndex]: description }));
    closeProductCreator();
  };

  const addLineItem = () => {
    userEditedLineItemsRef.current = true;
    const newLineItem = {
      line_number: lineItems.length + 1,
      item_description: '',
      product: null,
      quantity: 0,
      unit: units.length > 0 ? units[0].value : null,
      unit_price: 0,
      // Session-only UX flag set to true once the user confirms a
      // manual override of the catalog price on a linked line.
      unit_price_overridden: false,
      net_amount: 0,
      currency: invoiceData.currency || 'USD',
      vat_rate: null,
      vat_amount: 0,
      gross_amount: 0,
      gl_account: null
    };
    
    setLineItems([...lineItems, newLineItem]);
  };
  
  const removeLineItem = (index) => {
    userEditedLineItemsRef.current = true;
    const updated = lineItems.filter((_, i) => i !== index);
    updated.forEach((item, idx) => {
      item.line_number = idx + 1;
    });
    setLineItems(updated);
  };
  
  const updateLineItem = (index, field, value) => {
    userEditedLineItemsRef.current = true;
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };

    // Manual price edit on a linked line stamps the override flag so
    // the warning modal does not re-fire and the "Price overridden"
    // badge appears.
    if (field === 'unit_price' && updated[index].product) {
      updated[index].unit_price_overridden = true;
    }
    
    if (['quantity', 'unit_price', 'net_amount', 'vat_rate', 'custom_vat_percentage'].includes(field)) {
      calculateLineItemAmounts(updated, index, field);
    }
    
    setLineItems(updated);
  };
  
  const calculateLineItemAmounts = (items, index, changedField) => {
    const item = items[index];
    const quantity = parseFloat(item.quantity) || 0;
    const netAmount = parseFloat(item.net_amount) || 0;
    
    // Bidirectional calculation logic
    if (changedField === 'net_amount') {
      // If net amount was changed, calculate unit price from net amount and quantity
      if (quantity > 0) {
        item.unit_price = formatFigure(netAmount / quantity, 2, PRICE_DECIMALS);
      }
    } else if (changedField === 'quantity' || changedField === 'unit_price') {
      // If quantity or unit price was changed, calculate net amount.
      // Exact decimal product rounded to the cent, as the server does it.
      item.net_amount = lineNet(item.quantity, item.unit_price);
    }
    
    // VAT % through the one shared rule (vatPercentage.js): 'CUSTOM' reads the
    // typed %, a rate reads the picker's cache by id, no rate = 0. null = not
    // known yet (a rate still loading, or Custom % not typed yet): VAT and
    // gross stay "—", never 0.00.
    Object.assign(item, lineVatFigures(item.net_amount, resolveVatPercentage(item, vatPercentageCache.current)));
  };
  
  const userEditedLineItemsRef = useRef(false);
  const calculateTotals = () => {
    const netTotal = lineItems.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0);
    const vatTotal = lineItems.reduce((sum, item) => sum + parseFloat(item.vat_amount || 0), 0);
    const grossTotal = lineItems.reduce((sum, item) => sum + parseFloat(item.gross_amount || 0), 0);
    
    setInvoiceData(prev => {
      if (prev._capture_analysis_id && !userEditedLineItemsRef.current) {
        return prev;
      }
      // Sprint A: when the user picked the "Skip line items, header totals only"
      // capture mode, the synthetic line carries the captured header totals.
      // Preserve invoiceData's net/vat/gross until the user starts editing —
      // same suppression pattern as the _capture_analysis_id guard above.
      if (lineItems.some(item => item._capture_skip_lines) && !userEditedLineItemsRef.current) {
        return prev;
      }
      return {
        ...prev,
        net_amount: netTotal.toFixed(2),
        vat_amount: vatTotal.toFixed(2),
        gross_amount: grossTotal.toFixed(2)
      };
    });
  };
  
  // Validation functions
  const validateLineItem = (item, index) => {
    const errors = {};
    
    if (item.unit_price === null || item.unit_price === undefined || item.unit_price === '') {
      errors.unit_price = 'Unit price is required';
    }
    
    if (item.quantity === null || item.quantity === undefined || item.quantity === '') {
      errors.quantity = 'Quantity is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setLineItemErrors(prev => ({ ...prev, [index]: errors }));
      return false;
    } else {
      setLineItemErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return true;
    }
  };
  
  const validateAllLineItems = () => {
    let allValid = true;
    lineItems.forEach((item, index) => {
      if (!validateLineItem(item, index)) {
        allValid = false;
      }
    });
    return allValid;
  };
  
  const validateInvoiceTotal = () => {
    const total = parseFloat(invoiceData.gross_amount || 0);
    if (total <= 0) {
      setValidationErrors([{
        field: 'gross_amount',
        message: 'Invoice total must be greater than zero. Please add line items with valid amounts.'
      }]);
      setShowValidationModal(true);
      return false;
    }
    return true;
  };
  
  // Convert number to words
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    const [intPart, decPart] = num.toString().split('.');
    const intNum = parseInt(intPart);
    
    if (intNum === 0 && decPart) {
      const cents = parseInt(decPart.padEnd(2, '0').substring(0, 2));
      return cents === 0 ? 'Zero' : convertLessThanThousand(cents) + ' Cents';
    }
    
    let result = '';
    
    if (intNum >= 1000000) {
      result += convertLessThanThousand(Math.floor(intNum / 1000000)) + ' Million ';
    }
    if (intNum >= 1000) {
      result += convertLessThanThousand(Math.floor((intNum % 1000000) / 1000)) + ' Thousand ';
    }
    result += convertLessThanThousand(intNum % 1000);
    
    if (decPart) {
      const cents = parseInt(decPart.padEnd(2, '0').substring(0, 2));
      if (cents > 0) {
        result += ' and ' + convertLessThanThousand(cents) + ' Cents';
      }
    }
    
    return result.trim();
  };
  
  const handleNext = () => {
    // No validation on navigation - users can freely move between screens
    // Validation only happens on Save/Submit in Screen 3
    onNext();
  };
  
  return (
    <>
      <InvoiceValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        title="Line Items Validation"
        errors={validationErrors}
        fieldIds={validationFieldIds}
      />
      
      <div className="invoice-screen-content" style={{ overflow: 'visible' }}>
        {/* Line Items Table - Products ONLY (no vessel information for incoming invoices) */}
      <div className="invoice-form-section" style={{ overflow: 'visible' }}>
        <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <FontAwesomeIcon icon={faBox} /> Products
          </span>
          <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
            {invoiceData.hms_expense_number ? `Expense #: ${invoiceData.hms_expense_number}` : '(expense number will be generated on save)'}
          </span>
        </h2>
        
        <div className="line-items-table-container" style={{ overflow: 'visible' }}>
          <table className="line-items-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr style={{ fontSize: '10px' }}>
                <th style={{ minWidth: '250px', fontSize: '10px' }}>Item Description / Product Search</th>
                <th style={{ minWidth: '90px', maxWidth: '100px', fontSize: '10px' }}>Quantity</th>
                <th style={{ minWidth: '80px', fontSize: '10px' }}>Unit</th>
                <th style={{ minWidth: '120px', fontSize: '10px' }}>GL Account</th>
                <th style={{ minWidth: '80px', fontSize: '10px' }}>Currency</th>
                <th style={{ minWidth: '30px', maxWidth: '100px', fontSize: '10px' }}>Unit Price</th>
                <th style={{ minWidth: '110px', maxWidth: '100px', fontSize: '10px' }}>Net Amount</th>
                <th style={{ minWidth: '120px', fontSize: '10px' }}>VAT Rate</th>
                <th style={{ minWidth: '110px', fontSize: '10px' }}>VAT Amount</th>
                <th style={{ minWidth: '120px', maxWidth: '150px', width: '120px', fontSize: '10px' }}>Gross Amount</th>
                <th style={{ minWidth: '35px', width: '35px', fontSize: '10px' }}></th>
              </tr>
            </thead>
            <tbody style={{ overflow: 'visible' }}>
              {lineItems.map((item, index) => (
                <tr key={index} style={{ fontSize: '10px', overflow: 'visible' }}>
                  <td className="line-item-cell" ref={el => variantSearchRefs.current[index] = el} style={{ position: 'relative', overflow: 'visible' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <div style={{ position: 'relative' }}>
                        <FontAwesomeIcon 
                          icon={faSearch} 
                          style={{ 
                            position: 'absolute', 
                            left: '8px', 
                            top: '12px', 
                            color: '#666',
                            pointerEvents: 'none',
                            fontSize: '10px',
                            zIndex: 1
                          }} 
                        />
                        <textarea
                          id={`line_item_description_${index}`}
                          className="table-input"
                          style={{
                            fontSize: '10px',
                            paddingLeft: '24px',
                            paddingRight: '24px',
                            width: '100%',
                            minHeight: '32px',
                            resize: 'vertical',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            overflow: 'auto',
                            ...(!item.product && !isReadOnly ? {
                              backgroundColor: '#eff6ff',
                              borderColor: '#93c5fd',
                            } : {}),
                          }}
                          rows={2}
                          value={variantSearchTerms[index] || item.item_description || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setVariantSearchTerms(prev => ({ ...prev, [index]: value }));
                            setShowVariantDropdown(prev => ({ ...prev, [index]: true }));
                            
                            // If user is typing, allow manual entry
                            if (!showVariantDropdown[index]) {
                              updateLineItem(index, 'item_description', value);
                            }
                            clearFieldError(`line_item_description_${index}`);
                            
                            // Auto-resize as user types
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          onFocus={() => setShowVariantDropdown(prev => ({ ...prev, [index]: true }))}
                          placeholder="Search products or enter manually..."
                        />
                        {(variantSearchTerms[index] || item.item_description) && (
                          <FontAwesomeIcon 
                            icon={faTimes} 
                            onClick={(e) => {
                              e.stopPropagation();
                              setVariantSearchTerms(prev => ({ ...prev, [index]: '' }));
                              const updated = [...lineItems];
                              updated[index] = { ...updated[index], item_description: '', product: null };
                              setLineItems(updated);
                              setShowVariantDropdown(prev => ({ ...prev, [index]: false }));
                            }}
                            style={{ 
                              position: 'absolute', 
                              right: '8px', 
                              top: '12px', 
                              color: '#999',
                              cursor: 'pointer',
                              fontSize: '10px',
                              zIndex: 1
                            }}
                          />
                        )}
                      </div>
                      
                      {showVariantDropdown[index] && !isReadOnly && (
                        <div 
                          className="partner-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            minWidth: '300px',
                            maxWidth: '500px',
                            maxHeight: '250px',
                            overflowY: 'auto',
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            zIndex: 9999,
                            marginTop: '2px'
                          }}
                        >
                          {variantSearchLoading[index] && (
                            <div 
                              style={{ 
                                padding: '12px', 
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '10px'
                              }}
                            >
                              Searching products...
                            </div>
                          )}
                          
                          {!variantSearchLoading[index] && filteredVariants[index] && filteredVariants[index].length > 0 && (
                            filteredVariants[index].map((variant) => (
                              <div
                                key={variant.id}
                                className="partner-dropdown-item"
                                onClick={() => handleSelectVariant(index, variant)}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #eee',
                                  fontSize: '10px',
                                  lineHeight: '1.4'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ fontWeight: '500', color: '#333' }}>
                                  {variant.display_name}
                                </div>
                                {variant.quality && (
                                  <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                                    Quality: {variant.quality.substring(0, 50)}{variant.quality.length > 50 ? '...' : ''}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                          
                          {!variantSearchLoading[index] && (!filteredVariants[index] || filteredVariants[index].length === 0) && (
                            <div
                              style={{
                                padding: '12px',
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '10px'
                              }}
                            >
                              <div style={{ marginBottom: '6px', color: variantSearchError[index] ? '#b91c1c' : undefined }}>
                                {variantSearchError[index]
                                  ? variantSearchError[index]
                                  : (debouncedVariantSearches[index] && debouncedVariantSearches[index].length >= 2
                                    ? 'No products found.'
                                    : 'No matching product yet — create one below.')}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openProductCreator(
                                      index,
                                      variantSearchTerms[index] || item.item_description || debouncedVariantSearches[index] || ''
                                    );
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    fontSize: '10px',
                                    backgroundColor: '#3b82f6',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                  }}
                                >
                                  + Create product / service
                                </button>
                                <span style={{ fontSize: '10px', color: '#999' }}>or enter manually above</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Sprint D follow-up — per-line product status chip.
                          Three mutually exclusive states surfaced under the
                          description input so the user can see at a glance
                          whether the line is linked, has a fuzzy suggestion,
                          or needs picking/creating. Forced single-line via
                          whiteSpace: nowrap; long copy truncates with an
                          ellipsis on narrow columns. Hidden in read-only mode. */}
                      {item.product && !isReadOnly && (
                        <div
                          style={{
                            marginTop: '3px',
                            padding: '2px 6px',
                            backgroundColor: '#dcfce7',
                            border: '1px solid #86efac',
                            borderRadius: '3px',
                            fontSize: '9px',
                            color: '#166534',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title="This line is linked to a stock product variant"
                        >
                          Linked product
                        </div>
                      )}

                      {!item.product && !item._capture_product_suggestion && !isReadOnly && (
                        <div
                          style={{
                            marginTop: '3px',
                            padding: '2px 6px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #93c5fd',
                            borderRadius: '3px',
                            fontSize: '9px',
                            color: '#1e40af',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title="Click into the description field to search the product catalog or create a new product"
                        >
                          <span style={{ fontWeight: 500 }}>No match</span>
                          <span style={{ color: '#475569' }}> — click above to pick or create</span>
                        </div>
                      )}

                      {/* Sprint C — captured fuzzy-match suggestion chip.
                          Rendered when InvoiceCaptureModal couldn't find an
                          exact match but the top-1 search hit looks usable.
                          Mutually exclusive with auto-link (which sets
                          item.product directly), so we suppress the chip when
                          a product is already attached. */}
                      {item._capture_product_suggestion && !item.product && !isReadOnly && (
                        <div
                          style={{
                            marginTop: '4px',
                            padding: '6px 8px',
                            backgroundColor: '#fef9c3',
                            border: '1px solid #facc15',
                            borderRadius: '4px',
                            fontSize: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap',
                          }}
                          title="AI suggested this product based on the captured description"
                        >
                          <span style={{ color: '#854d0e', fontWeight: 500 }}>
                            Suggested:
                          </span>
                          <span style={{ color: '#1f2937', flex: '1 1 auto', minWidth: 0, wordBreak: 'break-word' }}>
                            {item._capture_product_suggestion.display_name || 'Product match'}
                          </span>
                          <button
                            type="button"
                            onClick={() => acceptCaptureSuggestion(index)}
                            style={{
                              padding: '2px 8px',
                              fontSize: '10px',
                              backgroundColor: '#3b82f6',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontWeight: 500,
                            }}
                          >
                            Link
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissCaptureSuggestion(index)}
                            style={{
                              padding: '2px 8px',
                              fontSize: '10px',
                              backgroundColor: '#fff',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '3px',
                              cursor: 'pointer',
                            }}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="line-item-cell">
                    <input
                      id={`line_item_quantity_${index}`}
                      type="number"
                      step={stepFor(QUANTITY_DECIMALS)}
                      min={stepFor(QUANTITY_DECIMALS)}
                      className={`table-input-number ${lineItemErrors[index]?.quantity ? 'error' : ''}`}
                      style={{ fontSize: '10px' }}
                      value={item.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // InvoiceLineItem.quantity stores QUANTITY_DECIMALS
                        // places (utils/precision.js). One more 400s the whole
                        // save, so refuse it here rather than at the API.
                        // See exceedsDecimals().
                        if (exceedsDecimals(value, QUANTITY_DECIMALS)) return;
                        updateLineItem(index, 'quantity', value);
                        clearFieldError(`line_item_quantity_${index}`);
                        if (parseFloat(value) <= 0) {
                          validateLineItem({ ...item, quantity: value }, index);
                        }
                      }}
                      onBlur={() => validateLineItem(item, index)}
                      onKeyPress={(e) => {
                        if (!/[0-9.]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="nr. + product"
                    />
                    {/* A bought fuel from a delivery: BDN or contract, and how far the typed figure is from the BDN. */}
                    <BdnQuantityNote quantity={item.quantity} bdnCheck={item.bdn_check} unit={item.unit_code || 'MT'} />
                  </td>
                  <td>
                    <select
                      className="table-select"
                      style={{ fontSize: '10px' }}
                      value={item.unit || ''}
                      onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                    >
                      <option value="">Select...</option>
                      {units.map(unit => (
                        <option key={unit.value} value={unit.value}>
                          {unit.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ overflow: 'visible' }}>
                    <SearchableSelect
                      fetchUrl="/api/invoicing/gl-accounts/"
                      params={glAccountParams}
                      displayFn={(gl) => `${gl.account_number} - ${gl.account_name}`}
                      value={item.gl_account || ''}
                      onChange={(id) => updateLineItem(index, 'gl_account', id)}
                      placeholder="Search GL..."
                      variant="table"
                      listOnOpen
                      pageSize={50}
                    />
                  </td>
                  <td>
                    <select
                      className="table-select"
                      style={{ fontSize: '10px' }}
                      value={item.currency}
                      onChange={(e) => updateLineItem(index, 'currency', e.target.value)}
                    >
                      {currencies.map(currency => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="line-item-cell">
                    <EditableUnitPriceCell
                      inputId={`line_item_unit_price_${index}`}
                      value={item.unit_price}
                      hasLinkedProduct={!!item.product}
                      isOverridden={!!item.unit_price_overridden}
                      disabled={false}
                      productLabel={item.item_description}
                      currency={item.currency}
                      className={`table-input-number ${lineItemErrors[index]?.unit_price ? 'error' : ''}`}
                      onConfirmedChange={(newValue) => {
                        updateLineItem(index, 'unit_price', newValue);
                        clearFieldError(`line_item_unit_price_${index}`);
                      }}
                      onBlur={() => validateLineItem(item, index)}
                    />
                  </td>
                  <td className="line-item-cell">
                    <input
                      id={`line_item_net_amount_${index}`}
                      type="number"
                      step={stepFor(AMOUNT_DECIMALS)}
                      min="0"
                      className="table-input-number"
                      style={{ fontSize: '10px' }}
                      value={item.net_amount}
                      onChange={(e) => {
                        // Net is money: AMOUNT_DECIMALS places. Refuse a
                        // further decimal at the keystroke rather than let the
                        // server refuse the save.
                        if (exceedsDecimals(e.target.value, AMOUNT_DECIMALS)) return;
                        updateLineItem(index, 'net_amount', e.target.value);
                      }}
                      placeholder="0.00"
                    />
                  </td>
                  <td className="line-item-cell" style={{ overflow: 'visible' }}>
                    {/* VAT cell: in CUSTOM mode we collapse the previous
                        SearchableSelect + separate "VAT %" input into a single
                        in-place editable percentage with an × to return to the
                        standard-rate select. Behaviours:
                          - Soft blue tint + bottom helper chip mark the row as
                            being in custom mode, mirroring the unmatched-product
                            pattern.
                          - Trailing % overlay appears once a value is typed, so
                            the user always sees "20%" rather than a bare number.
                          - Clearing the input back to empty (any path: select-all
                            + delete, repeated backspace, x-button) auto-reverts
                            to the SearchableSelect so the user doesn't get stuck
                            in CUSTOM mode with no value. */}
                    {item.vat_rate === 'CUSTOM' ? (
                      <div>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            className="table-input"
                            style={{
                              fontSize: '10px',
                              width: '100%',
                              paddingRight: '40px',
                              backgroundColor: '#eff6ff',
                              borderColor: '#93c5fd',
                            }}
                            placeholder="Custom VAT %"
                            value={item.custom_vat_percentage ?? ''}
                            min="0"
                            max="100"
                            step="0.01"
                            autoFocus
                            onChange={(e) => {
                              const newValue = e.target.value;
                              const previous = item.custom_vat_percentage;
                              const wasNonEmpty = previous !== null && previous !== undefined && String(previous) !== '';
                              if (newValue === '' && wasNonEmpty) {
                                const updated = [...lineItems];
                                updated[index] = {
                                  ...updated[index],
                                  vat_rate: null,
                                  custom_vat_percentage: null,
                                };
                                calculateLineItemAmounts(updated, index, 'vat_rate');
                                setLineItems(updated);
                                clearFieldError(`line_item_vat_rate_${index}`);
                              } else {
                                updateLineItem(index, 'custom_vat_percentage', newValue);
                              }
                            }}
                            title="Custom VAT percentage. Clear the value or click × to switch back to a standard VAT rate."
                          />
                          {item.custom_vat_percentage !== '' && item.custom_vat_percentage !== null && item.custom_vat_percentage !== undefined && (
                            <span
                              style={{
                                position: 'absolute',
                                right: '22px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#475569',
                                fontSize: '10px',
                                pointerEvents: 'none',
                                fontWeight: 500,
                              }}
                              aria-hidden="true"
                            >
                              %
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...lineItems];
                              updated[index] = {
                                ...updated[index],
                                vat_rate: null,
                                custom_vat_percentage: null,
                              };
                              calculateLineItemAmounts(updated, index, 'vat_rate');
                              setLineItems(updated);
                              clearFieldError(`line_item_vat_rate_${index}`);
                            }}
                            style={{
                              position: 'absolute',
                              right: '4px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'transparent',
                              border: 'none',
                              color: '#999',
                              cursor: 'pointer',
                              fontSize: '14px',
                              lineHeight: 1,
                              padding: '0 4px',
                            }}
                            title="Switch back to a standard VAT rate"
                          >
                            ×
                          </button>
                        </div>
                        <div
                          style={{
                            marginTop: '3px',
                            padding: '2px 6px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #93c5fd',
                            borderRadius: '3px',
                            fontSize: '9px',
                            color: '#1e40af',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title="This line uses a custom VAT percentage instead of a standard rate. Clear the value or click × to revert to a standard rate."
                        >
                          <span style={{ fontWeight: 500 }}>Custom VAT</span>
                        </div>
                      </div>
                    ) : (
                      <VatRateSelect
                        params={vatRateParams}
                        value={item.vat_rate || ''}
                        onChange={(id, selectedItem) => {
                          const updated = [...lineItems];
                          if (id === 'CUSTOM') {
                            updated[index] = { ...updated[index], vat_rate: 'CUSTOM', custom_vat_percentage: updated[index].custom_vat_percentage ?? '' };
                          } else {
                            if (selectedItem?.rate_percentage !== undefined) {
                              vatPercentageCache.current[id] = parseFloat(selectedItem.rate_percentage);
                            }
                            updated[index] = { ...updated[index], vat_rate: id ? parseInt(id) : null, custom_vat_percentage: null };
                          }
                          calculateLineItemAmounts(updated, index, 'vat_rate');
                          setLineItems(updated);
                          clearFieldError(`line_item_vat_rate_${index}`);
                        }}
                        onResolve={(resolvedItem) => {
                          if (resolvedItem?.id && resolvedItem?.rate_percentage !== undefined) {
                            vatPercentageCache.current[resolvedItem.id] = parseFloat(resolvedItem.rate_percentage);
                            // Lines left at "—" while this rate was unknown.
                            setLineItems(prev => fillPendingVatFigures(prev, vatPercentageCache.current));
                          }
                        }}
                        extraOptions={[{ id: 'CUSTOM', display: 'Custom %' }]}
                        id={`line_item_vat_rate_${index}`}
                      />
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      step={stepFor(AMOUNT_DECIMALS)}
                      min="0"
                      className="table-input-number"
                      style={{ fontSize: '10px' }}
                      value={item.vat_amount ?? ''}
                      onChange={(e) => {
                        // VAT is money: AMOUNT_DECIMALS places, same as net.
                        if (exceedsDecimals(e.target.value, AMOUNT_DECIMALS)) return;
                        const updated = [...lineItems];
                        updated[index] = { ...updated[index], vat_amount: e.target.value };
                        const netAmt = parseFloat(updated[index].net_amount) || 0;
                        const vatAmt = parseFloat(e.target.value) || 0;
                        updated[index].gross_amount = (netAmt + vatAmt).toFixed(2);
                        setLineItems(updated);
                        userEditedLineItemsRef.current = true;
                      }}
                      placeholder={item.vat_amount === null ? PENDING_FIGURE : '0.00'}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="table-input-calculated"
                      style={{ fontSize: '10px' }}
                      value={item.gross_amount === null ? PENDING_FIGURE : formatNumberByLocale(item.gross_amount, 2)}
                      readOnly
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-icon-danger"
                      onClick={() => removeLineItem(index)}
                      disabled={lineItems.length === 1}
                      style={{ fontSize: '20px', color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <button type="button" className="btn-add-line" onClick={addLineItem}>
            <FontAwesomeIcon icon={faPlus} /> Add Item
          </button>
        </div>
        
        {/* Number Format Selection - positioned above totals */}
        <div className="number-format-selector" style={{ marginTop: '12px', marginBottom: '8px', marginRight: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <div>
            <label className="form-label" style={{ marginBottom: '4px', display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#374151' }}>
              Number Format for PDF
            </label>
            <div style={{ 
              display: 'inline-flex',
              flexDirection: 'row', 
              gap: '0.5rem',
              padding: '0.35rem 0.5rem',
              background: '#f8f9ff',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              width: 'fit-content'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0' }}>
                <input
                  type="checkbox"
                  id="number-format-us-incoming"
                  className="due-date-checkbox"
                  checked={invoiceData.number_format === 'US' || !invoiceData.number_format}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setInvoiceData({ ...invoiceData, number_format: 'US' });
                    }
                  }}
                  disabled={isReadOnly}
                />
                <label htmlFor="number-format-us-incoming" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.75rem',
                  color: '#374151',
                  cursor: 'pointer',
                  userSelect: 'none',
                  margin: 0
                }}>
                  US Format (10,000.00)
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0' }}>
                <input
                  type="checkbox"
                  id="number-format-eu-incoming"
                  className="due-date-checkbox"
                  checked={invoiceData.number_format === 'EU'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setInvoiceData({ ...invoiceData, number_format: 'EU' });
                    }
                  }}
                  disabled={isReadOnly}
                />
                <label htmlFor="number-format-eu-incoming" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.75rem',
                  color: '#374151',
                  cursor: 'pointer',
                  userSelect: 'none',
                  margin: 0
                }}>
                  European Format (10.000,00)
                </label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Totals Summary */}
        <div className="invoice-totals-summary">
          <div className="left-column-totals">
            <div className="amount-in-words">
              <label>Gross amount in words:</label>
              <div className="words-display">
                {invoiceData.gross_amount && parseFloat(invoiceData.gross_amount) > 0
                  ? numberToWords(parseFloat(invoiceData.gross_amount)) + ` ${lineItems.length > 0 && lineItems[0].currency ? lineItems[0].currency : (invoiceData.currency || 'USD')}`
                  : 'Zero'}
              </div>
            </div>
          </div>
          
          <div className="totals-box">
            <div className="total-row">
              <span className="total-label">Net amount:</span>
              <span className="total-value">{formatNumberByLocale(invoiceData.net_amount, 2)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">VAT amount:</span>
              <span className="total-value">{formatNumberByLocale(invoiceData.vat_amount, 2)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">Gross amount:</span>
              <span className="total-value">{formatNumberByLocale(invoiceData.gross_amount, 2)}</span>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="screen-navigation">
          <button className="btn-secondary" type="button" onClick={onPrevious}>
            <FontAwesomeIcon icon={faBuilding} /> Previous: Header & Partner
          </button>
          <button className="btn-primary" type="button" onClick={handleNext}>
            Next: Bank & Notes <FontAwesomeIcon icon={faCalculator} />
          </button>
        </div>
      </div>
    </div>

    {/* Sprint D — Inline product creator. Renders the canonical 2-step
        StockItemForm + StockItemVariantForm flow when the user clicks
        "+ Create product" from the empty search dropdown. New variant is
        auto-attached to the line item that triggered the flow via
        handleInlineProductCreated. AP surface ⇒ purchase_price pre-fill so
        the new variant is findable on the next variant search. */}
    {productCreatorState.isOpen && (() => {
      const triggeringLine = lineItems[productCreatorState.lineIndex] || {};
      return (
        <InlineProductCreator
          isOpen={true}
          onClose={closeProductCreator}
          onProductCreated={handleInlineProductCreated}
          initialSearchTerm={productCreatorState.searchTerm}
          initialPriceField="purchase_price"
          initialUnitPrice={triggeringLine.unit_price || ''}
          initialCurrency={triggeringLine.currency || invoiceData.currency || 'USD'}
          initialQuantity={triggeringLine.quantity || ''}
          allowService
          onServiceCreated={handleInlineServiceCreated}
          initialServiceDescription={productCreatorState.searchTerm}
        />
      );
    })()}
    </>
  );
};

export default IncomingInvoiceScreen2;

